// Morrow — WhatsApp connect page (Settings → Connections → WhatsApp).
//
// Personal WhatsApp is a CONNECTION (an account the agent acts on for you —
// reads your chats, sends as you), not a Comms front-door channel. So it lives
// in Connections like Gmail/Slack, and this page hosts its QR link flow: link
// your own WhatsApp as a device (scan like WhatsApp Web), then Morrow can read +
// send on your behalf (every send gated by Allowance). Polls status so the QR
// appears and "connected" lands out-of-band.
import { Loader2Icon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { apiUrl } from "../../apiBase";
import { QRCodeSvg } from "../ui/qr-code";

type WaPersonalStatus = {
  status: "disconnected" | "pending" | "connected" | "logged_out";
  qr?: string;
  display?: string;
  waJid?: string;
};

const PRIMARY_BUTTON =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60";
const GHOST_BUTTON =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border/60 px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground disabled:opacity-60";

async function postJson(path: string, body: Record<string, unknown> = {}): Promise<void> {
  try {
    await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    /* ignore; the poll reflects the true state */
  }
}

export function WhatsAppConnectPage() {
  const [st, setSt] = useState<WaPersonalStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(apiUrl("/api/morrow/whatsapp-personal/status"), {
        headers: { accept: "application/json" },
      });
      if (res.ok) setSt((await res.json()) as WaPersonalStatus);
    } catch {
      /* ignore; the poll retries */
    }
  }, []);

  useEffect(() => {
    void refresh();
    pollRef.current = setInterval(() => void refresh(), 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refresh]);

  const link = useCallback(async () => {
    setBusy(true);
    await postJson("/api/morrow/whatsapp-personal/link");
    setBusy(false);
    void refresh();
  }, [refresh]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    await postJson("/api/morrow/whatsapp-personal/disconnect");
    setBusy(false);
    setSt({ status: "disconnected" });
  }, []);

  const status = st?.status ?? "disconnected";

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-6 px-4 py-8 sm:px-6">
      <header className="flex items-start gap-3">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border bg-white dark:bg-white">
          <img
            src="https://logos.composio.dev/api/whatsapp"
            alt=""
            aria-hidden="true"
            className="size-6 object-contain"
          />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-foreground">WhatsApp</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Link your own WhatsApp as a device so Morrow can read your chats and send messages on
            your behalf. Every send asks your approval first.
          </p>
        </div>
      </header>

      <div className="card-glass flex flex-col gap-4 rounded-xl border border-transparent p-5">
        {status === "connected" ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-500">
                <span className="size-1.5 rounded-full bg-emerald-500" />
                Connected
              </span>
              <span className="truncate text-sm text-muted-foreground">
                {st?.display ? st.display : "Linked"}
              </span>
            </div>
            <button
              type="button"
              className={GHOST_BUTTON}
              onClick={() => void disconnect()}
              disabled={busy}
            >
              Disconnect
            </button>
          </div>
        ) : status === "pending" ? (
          <div className="flex flex-col items-center gap-4">
            {st?.qr ? (
              <>
                <div className="rounded-xl border border-border bg-white p-3">
                  <QRCodeSvg value={st.qr} size={208} title="Link WhatsApp" />
                </div>
                <p className="max-w-sm text-center text-[13px] leading-[1.5] text-muted-foreground/80">
                  On your phone: WhatsApp →{" "}
                  <strong>Settings → Linked Devices → Link a Device</strong>, then scan this code.
                </p>
                <span className="text-xs text-muted-foreground/70">Waiting for the scan…</span>
              </>
            ) : (
              <span className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2Icon className="size-4 animate-spin" /> Generating your QR code…
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-start gap-3">
            {status === "logged_out" ? (
              <p className="text-[13px] leading-[1.5] text-muted-foreground/80">
                Morrow was unlinked from your WhatsApp. Link again to reconnect.
              </p>
            ) : null}
            <button
              type="button"
              className={PRIMARY_BUTTON}
              onClick={() => void link()}
              disabled={busy}
            >
              {busy ? <Loader2Icon className="size-4 animate-spin" /> : "Link WhatsApp"}
            </button>
          </div>
        )}
      </div>

      {status === "connected" ? (
        <p className="text-[13px] leading-[1.5] text-muted-foreground/70">
          Set up auto-replies and other automations for WhatsApp in{" "}
          <strong className="font-medium text-foreground">Workflows</strong> (left sidebar) — create
          a workflow, then add a WhatsApp trigger to it.
        </p>
      ) : null}
    </div>
  );
}
